/**
 * Reusable "Kayıtlı Filtre Setleri" bar.
 * - Lists the current user's private presets + team-shared presets for a given `scope`.
 * - Admin can save private OR shared (checkbox in the save dialog).
 * - Clicking a preset chip calls `onApply(filters, colFilters)`.
 * - Deleting removes it server-side (owner-only).
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bookmark, Plus, Users, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function SavedFilterPresets({ scope, currentFilters, currentColFilters, onApply }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveShared, setSaveShared] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/filter-presets?scope=${scope}`);
      setRows(r.data?.rows || []);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);

  const hasAny = (currentFilters && Object.values(currentFilters).some((v) => v !== "" && v != null)) ||
                 (currentColFilters && Object.keys(currentColFilters).length > 0);

  const openSaveDialog = () => {
    if (!hasAny) { toast.error("Önce filtre uygulayın"); return; }
    setSaveName("");
    setSaveShared(false);
    setShowSaveDialog(true);
  };

  const submitSave = async (e) => {
    e?.preventDefault?.();
    if (!saveName.trim()) { toast.error("İsim gerekli"); return; }
    setSaving(true);
    try {
      await api.post("/filter-presets", {
        scope,
        name: saveName.trim(),
        filters: currentFilters || {},
        col_filters: currentColFilters || {},
        shared: isAdmin && saveShared,
      });
      toast.success(saveShared ? "Takıma kaydedildi" : "Filtre kaydedildi");
      setShowSaveDialog(false);
      load();
    } catch (e) {
      if (e?.response?.status === 409) toast.error("Bu isimde kayıtlı filtre zaten var");
      else if (e?.response?.status === 403) toast.error("Paylaşımı yalnızca admin aktif edebilir");
      else toast.error("Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`"${name}" kayıtlı filtresi silinecek. Onaylıyor musunuz?`)) return;
    try {
      await api.delete(`/filter-presets/${id}`);
      toast.success("Silindi");
      load();
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Yalnızca sahibi silebilir");
      else toast.error("Silinemedi");
    }
  };

  const handleApply = (p) => {
    onApply(p.filters || {}, p.col_filters || {});
    toast.success(`"${p.name}" uygulandı`);
  };

  const privateRows = rows.filter((p) => !p.shared);
  const sharedRows  = rows.filter((p) =>  p.shared);

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap" data-testid={`saved-presets-${scope}`}>
        <div className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-slate-600">
          <Bookmark size={12} /> Kayıtlı Filtreler
        </div>
        {loading && <span className="text-[11px] text-slate-400">Yükleniyor...</span>}
        {!loading && rows.length === 0 && (
          <span className="text-[11px] italic text-slate-400">Henüz kayıt yok</span>
        )}
        {/* Private chips */}
        {privateRows.map((p) => (
          <span
            key={p.id}
            className="inline-flex items-center gap-1 bg-indigo-50 border border-indigo-200 text-indigo-800 text-[11px] font-semibold rounded-full pl-2 pr-1 py-0.5"
            data-testid={`preset-${scope}-${p.id}`}
          >
            <button type="button" onClick={() => handleApply(p)} className="hover:underline" title="Uygula">
              {p.name}
            </button>
            <button
              type="button"
              onClick={() => handleDelete(p.id, p.name)}
              className="w-4 h-4 rounded-full hover:bg-rose-100 text-rose-600 grid place-items-center"
              title="Sil"
              data-testid={`preset-delete-${p.id}`}
            >
              <X size={10} />
            </button>
          </span>
        ))}
        {/* Shared chips — team icon prefix, delete only for owner */}
        {sharedRows.map((p) => (
          <span
            key={p.id}
            className="inline-flex items-center gap-1 bg-amber-50 border border-amber-300 text-amber-900 text-[11px] font-semibold rounded-full pl-1.5 pr-1 py-0.5"
            data-testid={`preset-${scope}-${p.id}`}
            title={p.is_owner ? "Takım filtresi (sizin oluşturduğunuz)" : "Takım filtresi (admin tarafından paylaşıldı)"}
          >
            <Users size={10} className="shrink-0" />
            <button type="button" onClick={() => handleApply(p)} className="hover:underline" title="Uygula">
              {p.name}
            </button>
            {p.is_owner && (
              <button
                type="button"
                onClick={() => handleDelete(p.id, p.name)}
                className="w-4 h-4 rounded-full hover:bg-rose-100 text-rose-600 grid place-items-center"
                title="Sil"
                data-testid={`preset-delete-${p.id}`}
              >
                <X size={10} />
              </button>
            )}
          </span>
        ))}
        <button
          type="button"
          onClick={openSaveDialog}
          disabled={saving || !hasAny}
          className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border border-dashed border-slate-300 text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`preset-save-${scope}`}
          title={hasAny ? "Mevcut filtreleri kaydet" : "Önce filtre uygulayın"}
        >
          <Plus size={11} /> Kaydet
        </button>
      </div>

      {/* Save dialog */}
      {showSaveDialog && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => !saving && setShowSaveDialog(false)}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitSave}
            className="bg-white rounded-xl shadow-xl p-5 w-full max-w-sm space-y-4"
            data-testid={`preset-save-dialog-${scope}`}
          >
            <div>
              <div className="text-base font-bold text-slate-900 mb-0.5">Kayıtlı Filtre Oluştur</div>
              <div className="text-[11px] text-slate-500">Mevcut filtre kombinasyonu bu isim altında saklanır.</div>
            </div>
            <input
              type="text"
              autoFocus
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              placeholder="örn. Son 30 Gün - Yüksek Tutarlar"
              maxLength={80}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              data-testid={`preset-save-name-${scope}`}
            />
            {isAdmin && (
              <label className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md p-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={saveShared}
                  onChange={(e) => setSaveShared(e.target.checked)}
                  className="mt-0.5"
                  data-testid={`preset-save-shared-${scope}`}
                />
                <div className="flex-1">
                  <div className="text-xs font-bold text-amber-900 inline-flex items-center gap-1">
                    <Users size={11} /> Ekiple paylaş (tüm muhasebeciler görür)
                  </div>
                  <div className="text-[10px] text-amber-700 mt-0.5">
                    Paylaşılan filtreler takım genelinde görünür; yalnızca siz silebilirsiniz.
                  </div>
                </div>
              </label>
            )}
            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setShowSaveDialog(false)}
                disabled={saving}
                className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-md"
              >
                İptal
              </button>
              <button
                type="submit"
                disabled={saving || !saveName.trim()}
                className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-semibold rounded-md"
                data-testid={`preset-save-submit-${scope}`}
              >
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
